import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Avatar,
  Paper,
  Typography,
  FormControl,
  MenuItem,
  Select,
  useMediaQuery,
  Container,
  Badge,
  Fade,
  Chip,
  CircularProgress,
  Button,
  Modal,
  Backdrop
} from "@mui/material";
import dayjs from "dayjs";
import { useTheme } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveChallenge,
  selectLeaderboardData,
  selectLeaderboardLoading
} from "../../features/diary/diarySelectors";
import {
  getActiveChallenge,
  getCurrentLevelLeaderboard
} from "../../features/diary/diarySlice";
import useSignedUrl from "../../utils/useSignedUrl";
import getInitials from "../../utils/getInitials";
import useThemeColor from "../../utils/useTheme";
import { ReactComponent as Crown } from "../../assets/crown.svg";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ViewUserMeal from "../Modals/ViewUserMeals";
import { useCurrentUser } from "./UserContext";
import { useNavigate } from "react-router-dom";
import PointsSystem from "./PointsInfo";

const ImageItem = ({ sx, img, alt }) => {
  // const imgSrc = useSignedUrl(
  //   img,
  //   process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  // );

  return (
    <Box
      borderRadius="50%"
      sx={{
        ...sx,
        position: "relative",
        overflow: "hidden"
      }}
    >
      <Box
        component="img"
        src={img}
        borderRadius="50%"
        alt={alt || "avatar"}
        loading="lazy" // Add lazy loading for images
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover"
        }}
      />
    </Box>
  );
};

function getLastSixMonths() {
  const months = [];
  for (let i = 0; i < 6; i++) {
    const date = dayjs().subtract(i, "month");
    const name = date.format("MMMM YYYY");
    const value = date.format("MM/YYYY");
    months.push({ name, value });
  }
  return months;
}

const getTrophyIcon = (position) => {
  switch (position) {
    case 1:
      return <EmojiEventsIcon sx={{ color: "#ffcc80", fontSize: 22 }} />;
    case 2:
      return <WorkspacePremiumIcon sx={{ color: "#bbdefb", fontSize: 20 }} />;
    case 3:
      return <MilitaryTechIcon sx={{ color: "#a5d6a7", fontSize: 20 }} />;
    default:
      return null;
  }
};

const getPositionStyles = (position, styles) => {
  let config = {
    backgroundColor: styles.background,
    color: styles.color,
    borderColor: "transparent"
  };

  switch (position) {
    case 1:
      config = {
        backgroundColor: "rgba(255, 204, 128, 0.1)", // Matching borderColor
        color: "#E69500",
        borderColor: "#ffcc80"
      };
      break;
    case 2:
      config = {
        backgroundColor: "rgba(187, 222, 251, 0.1)", // Matching borderColor
        color: "#1976D2",
        borderColor: "#bbdefb"
      };
      break;
    case 3:
      config = {
        backgroundColor: "rgba(165, 214, 167, 0.1)", // Matching borderColor
        color: "#388E3C",
        borderColor: "#a5d6a7"
      };
      break;
    default:
      break;
  }

  return config;
};

const StyledPodium = ({
  user,
  isFirst,
  styles,
  userId,
  setIsViewingUserMeals
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const positionStyles = getPositionStyles(user.applyCss, styles);

  return (
    <Fade
      in
      timeout={500}
      style={{ transitionDelay: `${user.position * 100}ms` }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: isMobile ? "30%" : "33%",
          position: "relative",
          mt: isFirst ? 0 : 3,
          transform: isFirst ? "scale(1.1)" : "scale(1)",
          zIndex: isFirst ? 2 : 1,
          transition: "transform 0.3s ease"
        }}
        onClick={() => {
          setIsViewingUserMeals((prev) => ({ ...prev, createdByUser: user }));
        }}
      >
        <Badge
          sx={{
            "&:hover": {
              cursor: "pointer"
            }
          }}
          overlap="circular"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          badgeContent={
            user.applyCss === 1 ? (
              <Crown
                style={{
                  color: "#FFD700",
                  width: 24,
                  height: 24
                }}
              />
            ) : null
          }
        >
          <Avatar
            sx={{
              width: isMobile ? 70 : 90,
              height: isMobile ? 70 : 90,
              border: `3px solid ${positionStyles.borderColor}`,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              mb: 1,
              bgcolor: positionStyles.backgroundColor,
              color: positionStyles.color
            }}
          >
            {user?.profilePic ? (
              <ImageItem
                sx={{
                  width: "100%",
                  height: "100%"
                }}
                img={user?.profilePic}
                alt={user?.displayName || user?.fullName}
              />
            ) : (
              getInitials(user?.displayName || user?.fullName)
            )}
          </Avatar>
        </Badge>

        <Paper
          elevation={3}
          sx={{
            width: "100%",
            borderRadius: "16px",
            p: 2,
            mt: 1,
            background: positionStyles.backgroundColor,
            border: `1px solid ${positionStyles.borderColor}`,
            backdropFilter: "blur(10px)",
            textAlign: "center",
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "translateY(-5px)",
              cursor: "pointer"
            }
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            {getTrophyIcon(user.applyCss)}
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{
                bgcolor: positionStyles.borderColor,
                color: "#1c1c1c",
                px: 1,
                py: 0.25,
                borderRadius: "10px",
                ml: 0.5,
                fontSize: "0.7rem"
              }}
            >
              {user.position}
            </Typography>
          </Box>

          <Typography
            fontSize={isMobile ? "14px" : "16px"}
            color={styles.color}
            fontWeight="500"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 1
            }}
          >
            {user?.displayName || user?.fullName}
            {userId === user?._id ? " (Me)" : ""}
          </Typography>

          <Typography
            fontSize={isMobile ? "20px" : "24px"}
            color={positionStyles.color}
            fontWeight="bold"
          >
            {Math.floor(user?.totalProgress || 0)}
          </Typography>

          <Chip
            label="Pts"
            size="small"
            sx={{
              fontSize: "10px",
              height: "18px",
              backgroundColor: positionStyles.borderColor,
              color: "#1c1c1c",
              mt: 0.5
            }}
          />
          <Typography
            fontSize={isMobile ? "12px" : "13px"}
            color={"#F6541C"}
            mt={1}
          >
            ({Math.round(user?.total800gGrams || 0)})
          </Typography>
        </Paper>
      </Box>
    </Fade>
  );
};

const Leaderboard = ({
  levelId,
  selectedChallengeId,
  isViewingUserMeals,
  setIsViewingUserMeals
}) => {
  const styles = useThemeColor();
  const lastSixMonths = getLastSixMonths();
  const [value, setValue] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isModalOpening, setIsModalOpening] = useState(false);
  const theme = useTheme();
  const leaderboardLoading = useSelector(selectLeaderboardLoading);
  const leaderBoardData = useSelector(selectLeaderboardData);
  const activeChallenge = useSelector(selectActiveChallenge);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // More efficient modal handlers using useCallback
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    setValue(null);

    dispatch(
      getActiveChallenge({
        date: dayjs().format("YYYY-MM-DD"),
        // levelId: levelId,
        getAllChallenges: true
      })
    );
  }, [dispatch, levelId, selectedChallengeId]);

  // Set default value when data is loaded
  useEffect(() => {
    if (!selectedChallengeId && activeChallenge?.[0]?._id) {
      setValue(activeChallenge[0]._id);
    } else if (selectedChallengeId) {
      setValue(selectedChallengeId);
    }
  }, [activeChallenge, selectedChallengeId]);

  // Load leaderboard data
  useEffect(() => {
    if (value && levelId) {
      const data = { challengeId: value, date: dayjs().format("YYYY-MM-DD") };
      dispatch(
        getCurrentLevelLeaderboard({
          levelId,
          data
        })
      );
    }
  }, [dispatch, levelId, value]);

  // Optimized handlers for buttons
  const handleNavigateToPoints = useCallback(() => {
    if (!isNavigating) {
      setIsNavigating(true);
      // Use requestAnimationFrame to ensure UI updates before navigation
      requestAnimationFrame(() => {
        navigate(
          `/point-stats?included=true&challenge=${value}&fromLeaderboard=true`
        );
        // Reset after a delay to prevent accidental double clicks
        setTimeout(() => setIsNavigating(false), 1000);
      });
    }
  }, [navigate, value, isNavigating]);

  const handleOpenScoringSystem = useCallback(() => {
    if (!isModalOpening) {
      setIsModalOpening(true);
      // Use requestAnimationFrame to ensure UI updates before modal opens
      requestAnimationFrame(() => {
        handleOpen();
        // Reset after a delay
        setTimeout(() => setIsModalOpening(false), 1000);
      });
    }
  }, [isModalOpening, handleOpen]);

  // Define top 3 users and other users based on array index
  const topUsers = leaderBoardData.slice(0, 3).map((user, index) => ({
    ...user,
    applyCss: index + 1
  }));

  // Filter out the current user from other users if they're in that list
  const currentUserInOthers = leaderBoardData
    .slice(3)
    .find((user) => user?._id === currentUser?.userId);
  const otherUsers = leaderBoardData
    .slice(3)
    .filter((user) => user?._id !== currentUser?.userId);

  return Object.keys(isViewingUserMeals).length > 0 ? (
    <ViewUserMeal
      isViewingUserMeals={isViewingUserMeals}
      setIsViewingUserMeals={setIsViewingUserMeals}
    />
  ) : (
    <Container maxWidth="md" sx={{ pb: { xs: 0, md: 4 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%"
        }}
      >
        <FormControl
          sx={{
            width: { xs: "100%", md: "60%" },
            mb: 4,
            ".MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.05)",
              backdropFilter: "blur(4px)",
              "& fieldset": {
                borderColor: styles.lightGreyColor
              },
              "&:hover fieldset": {
                borderColor: styles.color
              },
              "&.Mui-focused fieldset": {
                borderColor: styles.color
              }
            }
          }}
        >
          <Select
            labelId="challenge-select-label"
            name="challenge"
            value={value}
            displayEmpty
            renderValue={(selected) => {
              const selectedChallenge = activeChallenge?.find(
                (item) => item?._id === selected
              );
              return selectedChallenge ? (
                <Typography noWrap>
                  {selectedChallenge?.title} (
                  {selectedChallenge?.diaryLevelName})
                </Typography>
              ) : (
                "Select Challenge"
              );
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  backdropFilter: "blur(10px)"
                }
              },
              MenuListProps: {
                sx: {
                  padding: 0,
                  ".Mui-selected": {
                    backgroundColor: `${styles.color} !important`,
                    color: `${styles.background} !important`
                  }
                }
              }
            }}
            sx={{
              color: styles.color,
              "& .MuiSvgIcon-root": {
                color: styles.color
              }
            }}
            onChange={(e) => setValue(e.target.value)}
          >
            {activeChallenge?.length ? (
              activeChallenge?.map((item) => (
                <MenuItem
                  key={item?._id}
                  sx={{
                    backgroundColor: `${styles.background} !important`,
                    color: `${styles.color} !important`
                  }}
                  value={item?._id}
                >
                  <Box>
                    <Typography fontWeight="medium">{item?.title}</Typography>
                    <Typography variant="caption" display="block">
                      {item?.diaryLevelName} •{" "}
                      {dayjs.utc(item?.start_date).format("MMM DD")} -{" "}
                      {dayjs.utc(item?.end_date).format("MMM DD, YYYY")}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No challenges available</MenuItem>
            )}
          </Select>
        </FormControl>

        {leaderboardLoading ? (
          <CircularProgress />
        ) : activeChallenge?.length && leaderBoardData?.length ? (
          <>
            <Typography
              variant="body2"
              fontWeight="bold"
              fontStyle={"italic"}
              sx={{ color: styles.color, mb: isMobile ? 1.5 : 3 }}
            >
              NOTE: Points logged for the current day display tomorrow.
            </Typography>
            <Box mb={5} display="flex" gap={4}>
              {/* Enhanced My Points button with loading state */}
              <Button
                onClick={handleNavigateToPoints}
                disabled={isNavigating}
                sx={{
                  textTransform: "none",
                  background: isNavigating ? "#2a6ea5" : "#368AD0", // Visual feedback during loading
                  color: "#FFFF",
                  gap: "8px",
                  borderRadius: "4px",
                  alignSelf: "center",
                  // padding: isMobile ? "12px 24px" : "8px 16px",
                  // width: isMobile ? "100%" : "auto",
                  // fontSize: isMobile ? "16px" : "14px",
                  position: "relative",
                  zIndex: 5, // Ensure button is above other elements
                  "&:active": {
                    background: "#2a6ea5" // Visual feedback on press
                  }
                }}
              >
                {isNavigating ? "Loading..." : "My Points"}
              </Button>

              {/* Enhanced Scoring System button with loading state */}
              <Button
                variant="contained"
                onClick={handleOpenScoringSystem}
                disabled={isModalOpening}
                sx={{
                  background: isModalOpening ? "#d93600" : "#FF4000", // Visual feedback during loading
                  textTransform: "none",
                  color: "#FCFCFC",
                  gap: "8px",
                  borderRadius: "4px",
                  // padding: isMobile ? "12px 24px" : "8px 16px",
                  // width: isMobile ? "100%" : "auto",
                  // fontSize: isMobile ? "16px" : "14px",
                  position: "relative",
                  zIndex: 5, // Ensure button is above other elements
                  "&:active": {
                    background: "#d93600" // Visual feedback on press
                  }
                }}
              >
                {isModalOpening ? "Opening..." : "Scoring System"}
              </Button>
            </Box>
            {/* Top 3 Users - Podium */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
                width: "100%",
                mb: 8
              }}
            >
              {/* Second Place */}
              {topUsers[1] && (
                <StyledPodium
                  user={topUsers[1]}
                  isFirst={false}
                  userId={currentUser?.userId}
                  styles={styles}
                  setIsViewingUserMeals={setIsViewingUserMeals}
                />
              )}

              {/* First Place */}
              {topUsers[0] && (
                <StyledPodium
                  user={topUsers[0]}
                  isFirst={true}
                  userId={currentUser?.userId}
                  styles={styles}
                  setIsViewingUserMeals={setIsViewingUserMeals}
                />
              )}

              {/* Third Place */}
              {topUsers[2] && (
                <StyledPodium
                  user={topUsers[2]}
                  isFirst={false}
                  styles={styles}
                  userId={currentUser?.userId}
                  setIsViewingUserMeals={setIsViewingUserMeals}
                />
              )}
            </Box>
            {/* Other Users List */}
            <Box
              width="100%"
              sx={{
                position: "relative",
                // Add containment for better scroll performance
                contain: "content",
                // Make sure the list has a finite height to optimize rendering
                // maxHeight: otherUsers.length > 10 ? "500px" : "auto",
                // overflowY: otherUsers.length > 10 ? "auto" : "visible",
                // Use passive scroll listeners for better performance
                "&::-webkit-scrollbar": {
                  width: "8px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: `${styles.color}40`,
                  borderRadius: "4px"
                }
              }}
            >
              {currentUserInOthers && (
                <Fade in timeout={600}>
                  <Paper
                    onClick={() => {
                      setIsViewingUserMeals((prev) => ({
                        ...prev,
                        createdByUser: currentUserInOthers
                      }));
                    }}
                    elevation={3}
                    sx={{
                      display: "flex",
                      position: "sticky",
                      top: 0,
                      width: "100%",
                      backgroundColor: `${styles.color}15`,
                      backdropFilter: "blur(10px)",
                      color: styles.color,
                      alignItems: "center",
                      p: 1,
                      mb: 3,
                      borderRadius: "12px",
                      overflow: "hidden",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      zIndex: 10,
                      boxShadow: "0 5px 20px rgba(0,0,0,0.2)",
                      border: `2px solid ${styles.color}40`,
                      "&:hover": {
                        cursor: "pointer",
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.25)"
                      },
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "3px",
                        backgroundColor: styles.color,
                        opacity: 0.8
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        width: 35,
                        fontWeight: "bold",
                        color: styles.color,
                        opacity: 0.7
                      }}
                    >
                      #{currentUserInOthers.position}
                    </Typography>

                    {currentUserInOthers?.profilePic ? (
                      <ImageItem
                        sx={{
                          width: 56,
                          height: 56,
                          mr: 2,
                          ml: 1,
                          border: `3px solid ${styles.color}40`,
                          boxShadow: "0 2px 10px rgba(0,0,0,0.15)"
                        }}
                        img={currentUserInOthers?.profilePic}
                        alt={currentUserInOthers?.displayName}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          mr: 2,
                          ml: 1,
                          bgcolor: `${styles.color}20`,
                          color: styles.color,
                          border: `3px solid ${styles.color}40`,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                        }}
                      >
                        {getInitials(
                          currentUserInOthers?.displayName ||
                            currentUserInOthers?.fullName
                        )}
                      </Avatar>
                    )}

                    <Box
                      flexGrow={1}
                      display={isMobile ? "block" : "flex"}
                      alignItems={"center"}
                      gap="10px"
                    >
                      <Box
                        sx={{
                          display: { xs: "flex" },
                          gap: { xs: "5px", md: 1 }
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="medium"
                          style={
                            isMobile
                              ? {
                                  maxWidth: "120px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }
                              : {}
                          }
                        >
                          {currentUserInOthers?.displayName ||
                            currentUserInOthers?.fullName}{" "}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium">
                          (Me)
                        </Typography>
                      </Box>
                      <Typography
                        fontSize={isMobile ? "12px" : "14px"}
                        color={"#F6541C"}
                      >
                        ({Math.round(currentUserInOthers?.total800gGrams || 0)})
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        backgroundColor: `${styles.color}20`,
                        borderRadius: "10px",
                        py: 1.2,
                        px: 2.5
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: styles.color }}
                      >
                        {Math.floor(currentUserInOthers.totalProgress || 0)}
                      </Typography>
                      <Typography
                        fontSize={"10px"}
                        sx={{
                          color: styles.color,
                          opacity: 0.8,
                          letterSpacing: "0.5px",
                          fontWeight: "bold"
                        }}
                      >
                        Pts
                      </Typography>
                    </Box>
                  </Paper>
                </Fade>
              )}
              {otherUsers.map((user, idx) => (
                <Fade
                  in
                  timeout={400}
                  style={{ transitionDelay: `${Math.min(idx, 10) * 50}ms` }}
                  key={user?._id}
                >
                  <Paper
                    onClick={() => {
                      setIsViewingUserMeals((prev) => ({
                        ...prev,
                        createdByUser: user
                      }));
                    }}
                    elevation={1}
                    sx={{
                      display: "flex",
                      backgroundColor: "rgba(255,255,255,0.03)",
                      backdropFilter: "blur(4px)",
                      color: styles.color,
                      alignItems: "center",
                      p: 1,
                      px: 1,
                      mb: 1.5,
                      borderRadius: "12px",
                      overflow: "hidden",
                      position: "relative",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        cursor: "pointer",
                        transform: "translateY(-2px)",
                        boxShadow: "0 5px 15px rgba(0,0,0,0.1)"
                      },
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "3px",
                        backgroundColor: styles.color,
                        opacity: 0.6
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        width: 35,
                        fontWeight: "bold",
                        color: styles.color,
                        opacity: 0.6
                      }}
                    >
                      #{user.position}
                    </Typography>

                    {user?.profilePic ? (
                      <ImageItem
                        sx={{
                          width: 48,
                          height: 48,
                          mr: 2,
                          ml: 1,
                          border: `2px solid ${styles.color}20`
                        }}
                        img={user?.profilePic}
                        alt={user?.displayName}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          mr: 2,
                          ml: 1,
                          bgcolor: `${styles.color}15`,
                          color: styles.color,
                          border: `2px solid ${styles.color}20`
                        }}
                      >
                        {getInitials(user?.displayName || user?.fullName)}
                      </Avatar>
                    )}

                    <Box
                      flexGrow={1}
                      display={isMobile ? "block" : "flex"}
                      alignItems={"center"}
                      gap="10px"
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        style={
                          isMobile
                            ? {
                                maxWidth:
                                  currentUser?.userId === user?._id
                                    ? "120px"
                                    : "140px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }
                            : {}
                        }
                      >
                        {user?.displayName || user?.fullName}
                      </Typography>
                      <Typography
                        fontSize={isMobile ? "12px" : "14px"}
                        color={"#F6541C"}
                      >
                        ({Math.round(user?.total800gGrams || 0)})
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        backgroundColor: `${styles.color}10`,
                        borderRadius: "10px",
                        py: 1,
                        px: 2
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: styles.color }}
                      >
                        {Math.floor(user.totalProgress || 0)}
                      </Typography>
                      <Typography
                        fontSize={"10px"}
                        sx={{
                          color: styles.color,
                          opacity: 0.8,
                          letterSpacing: "0.5px"
                        }}
                      >
                        Pts
                      </Typography>
                    </Box>
                  </Paper>
                </Fade>
              ))}
            </Box>

            <Modal
              open={open}
              onClose={handleClose}
              aria-labelledby="points-modal-title"
              closeAfterTransition
              BackdropComponent={Backdrop}
              BackdropProps={{
                timeout: 500
              }}
            >
              <Fade in={open}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", md: "40%" },
                    bgcolor: "#1c1c1c",
                    borderRadius: "10px",
                    boxShadow: 24,
                    p: 4,
                    outline: "none"
                  }}
                >
                  {/* Modal Title */}
                  <Typography
                    id="points-modal-title"
                    variant="h6"
                    component="h2"
                    gutterBottom
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      color: "#368AD0",
                      marginBottom: "15px"
                    }}
                  >
                    Points System
                  </Typography>

                  <PointsSystem
                    type={
                      activeChallenge?.find(
                        (item) => item?._id === selectedChallengeId
                      )?.type
                    }
                  />

                  {/* Close Button */}
                  <Box display="flex" justifyContent="center" marginTop={3}>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      sx={{
                        textTransform: "none",
                        color: "#368AD0",
                        borderColor: "#368AD0",
                        "&:hover": {
                          backgroundColor: "#368AD0",
                          color: "#fff"
                        },
                        borderRadius: "8px",
                        padding: "8px 16px"
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Modal>
          </>
        ) : (
          <Paper
            elevation={2}
            sx={{
              p: 4,
              borderRadius: "16px",
              textAlign: "center",
              backgroundColor: "rgba(255,255,255,0.03)",
              backdropFilter: "blur(4px)"
            }}
          >
            <Typography color={styles.color}>
              No leaderboard data available{" "}
              {activeChallenge?.length ? `for this challenge` : ""} yet.
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Leaderboard;
